// apps/web/src/pages/compras/conferencia-nota-page.tsx — Fase 13, Plano 22 (COMP-03, EST-10, EST-12, D-08, D-10, D-15..D-18).
// Conferência e recebimento da NF de compra (`/compras/notas/conferencia?id=`). O sistema SUGERE (pedido, matéria-prima, fator,
// local); o usuário ajusta item a item e NADA é gravado antes de "Confirmar recebimento": a tela só guarda estado local e envia
// um único POST /receber com o payload montado por `montarPayloadRecebimento` (lib/compras/conferencia.ts).
import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Copy, FileWarning, Loader2, RefreshCw, UserCheck } from 'lucide-react';
import type { ConferenciaNotaCompra, ItemConferencia, LocalizacaoLookupEstoque, MaterialEntity, MaterialLookupCompras, ResultadoRecebimentoNota } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { MaterialFormModal } from '../cadastros/materiais-form';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../../components/ui/breadcrumb';
import { formatarCnpj } from '../../lib/compras/cnpj';
import {
  MENSAGEM_FALHA_RECEBIMENTO,
  aplicarLocalPadrao,
  alternarPedido,
  atualizarItem,
  chaveAbreviada,
  confirmarFornecedorLocal,
  definirFisica,
  definirSemPedido,
  escolherMaterial,
  estadoInicial,
  linhasDeSucesso,
  mensagemErroRecebimento,
  montarPayloadRecebimento,
  motivoDoBloqueio,
  pedidoDaNotaPendente,
  podeConfirmar,
  resumoRodape,
  semPedidoEfetivo,
  textoConfirmacao,
  type ErroRecebimento,
  type EstadoConferencia,
  type MaterialEscolhido,
  type LinhaSucessoRecebimento,
} from '../../lib/compras/conferencia';
import { disponivelParaEstorno } from '../../lib/compras/estorno';
import { navegar } from '../../lib/compras/navegar';
import { apresentacaoSituacaoNota } from '../../lib/compras/notas';
import { emModoRevisaoFiscal } from '../../lib/compras/omie-entradas';
import { apiMutate } from '../../lib/csrf-fetch';
import { obterSessaoAtual } from '../../lib/session';
import { formatarData, formatarMoeda, formatarQuantidade } from '../../lib/vendas/formatacao';
import { ConferenciaItemLinha } from './conferencia-item-linha';
import { DefinirFatorDialog, VincularMaterialDialog, materialDoLookup } from './conferencia-dialogs';
import { DevolverFornecedorDialog, EstornarEntradaDialog, type ItemEstornoAlvo } from './estorno-devolucao-dialogs';
import { FornecedorSheet } from './fornecedor-sheet';
import { OmieEntradaReviewDialog } from './omie-entrada-review-dialog';

type ErroCarga = 'carga' | 'nao-encontrado' | null;

const ROTULO_COLUNA = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';

export function ConferenciaNotaPage() {
  const id = new URLSearchParams(window.location.search).get('id');
  const [conferencia, setConferencia] = useState<ConferenciaNotaCompra | null>(null);
  const [estado, setEstado] = useState<EstadoConferencia | null>(null);
  const [locais, setLocais] = useState<readonly LocalizacaoLookupEstoque[]>([]);
  const locaisRef = useRef<readonly LocalizacaoLookupEstoque[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<ErroCarga>(null);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [confirmacaoAberta, setConfirmacaoAberta] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<ErroRecebimento | null>(null);
  const [sucesso, setSucesso] = useState<{ readonly resultado: ResultadoRecebimentoNota; readonly linhas: readonly LinhaSucessoRecebimento[] } | null>(null);
  const [fornecedorAberto, setFornecedorAberto] = useState(false);
  const [chaveCopiada, setChaveCopiada] = useState(false);
  const [vincularItemId, setVincularItemId] = useState<string | null>(null);
  const [fatorItemId, setFatorItemId] = useState<string | null>(null);
  const [cadastrarItemId, setCadastrarItemId] = useState<string | null>(null);
  const [errosItem, setErrosItem] = useState<Readonly<Record<string, string>>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  // Estorno/devolução de item recebido (13-24): o diálogo faz a própria escrita; a página só guarda o alvo e recarrega a nota.
  const [alvoEstorno, setAlvoEstorno] = useState<{ readonly tipo: 'estornar' | 'devolver'; readonly itemId: string } | null>(null);
  const [mensagemEstorno, setMensagemEstorno] = useState<string | null>(null);
  const [revisaoOmieAberta, setRevisaoOmieAberta] = useState(false);
  const propostaFiscalId = new URLSearchParams(window.location.search).get('propostaFiscalId');
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  // A autorização real é do servidor; aqui as ações só aparecem para quem pode.
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo && sessao.autenticado) setPermissoes(sessao.usuario.permissions);
    });
    return () => {
      vivo = false;
    };
  }, []);

  // Locais ativos: dão o nome do local nas mensagens e o padrão "Recebimento" quando o material não tem preferencial.
  useEffect(() => {
    let vivo = true;
    fetch('/api/v1/estoque/lookups/localizacoes', { credentials: 'include' })
      .then((res) => (res.ok ? (res.json() as Promise<LocalizacaoLookupEstoque[]>) : []))
      .then((lista) => {
        if (!vivo) return;
        locaisRef.current = lista;
        setLocais(lista);
        setEstado((atual) => (atual ? aplicarLocalPadrao(atual, lista) : atual));
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, []);

  const buscar = useCallback(async (): Promise<ConferenciaNotaCompra> => {
    const res = await fetch(`/api/v1/compras/notas/${encodeURIComponent(id ?? '')}/conferencia`, { credentials: 'include' });
    if (res.status === 404 || res.status === 400) throw new Error('nao-encontrado');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as ConferenciaNotaCompra;
  }, [id]);

  const carregar = useCallback(async (): Promise<void> => {
    if (!id) {
      setErroCarga('nao-encontrado');
      setCarregando(false);
      return;
    }
    setCarregando(true);
    setErroCarga(null);
    try {
      const atual = await buscar();
      if (!montado.current) return;
      setConferencia(atual);
      setEstado(estadoInicial(atual, locaisRef.current));
      setErroEnvio(null);
    } catch (falha) {
      if (montado.current) setErroCarga(falha instanceof Error && falha.message === 'nao-encontrado' ? 'nao-encontrado' : 'carga');
    } finally {
      if (montado.current) setCarregando(false);
    }
  }, [id, buscar]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  function nomeDoLocal(localizacaoId: string): string {
    return locais.find((local) => local.id === localizacaoId)?.nome ?? '';
  }

  async function copiarChave(): Promise<void> {
    if (!conferencia) return;
    try {
      await navigator.clipboard.writeText(conferencia.nota.chaveAcesso);
      setChaveCopiada(true);
      window.setTimeout(() => montado.current && setChaveCopiada(false), 3000);
    } catch {
      setChaveCopiada(false);
    }
  }

  async function confirmarRecebimento(): Promise<void> {
    if (!estado || enviando) return;
    setEnviando(true);
    setErroEnvio(null);
    setErrosItem({});
    try {
      // Única saída para o servidor: nenhum vínculo é gravado antes deste clique (COMP-03, D-15).
      const res = await apiMutate(`/api/v1/compras/notas/${encodeURIComponent(estado.notaId)}/receber`, {
        method: 'POST',
        body: montarPayloadRecebimento(estado),
      });
      const corpo: unknown = await res.json().catch(() => null);
      if (!montado.current) return;
      if (!res.ok) {
        // Diálogo permanece aberto; escolhas e justificativas ficam como estão.
        const erro = mensagemErroRecebimento(res.status, corpo, estado.itens);
        setErroEnvio(erro);
        setErrosItem(erro.tipo === 'item' && erro.notaCompraItemId ? { [erro.notaCompraItemId]: erro.mensagem } : {});
        return;
      }
      const resultado = corpo as ResultadoRecebimentoNota;
      // O custo médio "agora" vem da conferência recarregada; se a recarga falhar a linha sai sem esse trecho.
      let recarregada: ConferenciaNotaCompra | null = null;
      try {
        recarregada = await buscar();
      } catch {
        recarregada = null;
      }
      if (!montado.current) return;
      const custos = new Map<string, number | null>(recarregada ? recarregada.itens.map((item) => [item.notaCompraItemId, item.custoMedioAtual]) : []);
      if (recarregada) setConferencia(recarregada);
      setSucesso({
        resultado,
        linhas: linhasDeSucesso(resultado, estado, custos, nomeDoLocal, (valor) => formatarQuantidade(valor)),
      });
      setConfirmacaoAberta(false);
    } catch {
      if (montado.current) setErroEnvio({ mensagem: MENSAGEM_FALHA_RECEBIMENTO, tipo: 'geral', notaCompraItemId: null, campo: null, semEntrada: false });
    } finally {
      if (montado.current) setEnviando(false);
    }
  }

  /** Estorno/devolução concluído: mostra a mensagem e recarrega a nota (situação "Devolvida"/"Devolvida em parte"). */
  function aposEstorno(mensagem: string): void {
    setSucesso(null);
    setAviso(null);
    setMensagemEstorno(mensagem);
    void carregar();
  }

  function aplicarMaterial(notaCompraItemId: string, material: MaterialEscolhido): void {
    setErrosItem({});
    setEstado((atual) => (atual ? escolherMaterial(atual, notaCompraItemId, material) : atual));
  }

  /** Matéria-prima recém-cadastrada: busca no lookup de Compras (traz unidade e fator) e a seleciona no item. */
  async function selecionarMaterialCriado(notaCompraItemId: string, criado: MaterialEntity): Promise<void> {
    try {
      const params = new URLSearchParams({ busca: criado.codigo, incluirOutros: 'true' });
      if (conferencia?.fornecedor) params.set('fornecedorId', conferencia.fornecedor.id);
      const res = await fetch(`/api/v1/compras/lookups/materiais?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const lista = (await res.json()) as MaterialLookupCompras[];
      const encontrado = lista.find((material) => material.id === criado.id);
      if (!encontrado) throw new Error('nao-encontrado');
      if (montado.current) aplicarMaterial(notaCompraItemId, materialDoLookup(encontrado));
    } catch {
      if (montado.current) setAviso('A matéria-prima foi cadastrada, mas não foi possível selecioná-la. Use "Vincular matéria-prima".');
    }
  }

  if (carregando && !conferencia) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 p-6" aria-busy="true" aria-label="Carregando conferência da nota">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (erroCarga || !conferencia || !estado) {
    return (
      <div className="mx-auto w-full max-w-7xl p-6">
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">
              {erroCarga === 'nao-encontrado' ? 'Nota de compra não encontrada.' : 'Não foi possível carregar a nota de compra. Verifique a conexão e tente novamente.'}
            </p>
            {erroCarga === 'nao-encontrado' ? (
              <Button asChild variant="outline">
                <a href="/compras/notas">Voltar</a>
              </Button>
            ) : (
              <Button type="button" variant="outline" onClick={() => void carregar()}>
                <RefreshCw aria-hidden="true" />
                Tentar novamente
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const { nota, fornecedor, emitente } = conferencia;
  const situacao = apresentacaoSituacaoNota(nota.situacao);
  const podeReceber = permissoes.includes('compras:receber');
  const podeGerenciar = permissoes.includes('compras:gerenciar');
  // L-69 (17.1-27): quem só tem financeiro:aprovar-fiscal (Faturamento) abre a nota em modo de revisão: recebimento e estorno ficam com Compras.
  const modoRevisaoFiscal = emModoRevisaoFiscal(permissoes);
  const somenteLeitura = sucesso !== null || nota.situacao !== 'A_CONFERIR' || modoRevisaoFiscal;
  const propostaDaNota = propostaFiscalId ?? nota.propostaFiscalId ?? null;
  const podeConfigurarEstoque = permissoes.includes('estoque:configurar');
  const podeCadastrarMaterial = permissoes.includes('cadastros:gerenciar');
  const itemServidor = (itemId: string): ItemConferencia | undefined => conferencia.itens.find((candidato) => candidato.notaCompraItemId === itemId);
  const recebidoDoItem = (itemId: string): ItemConferencia['recebido'] => {
    const doServidor = itemServidor(itemId)?.recebido ?? null;
    if (doServidor || !sucesso) return doServidor;
    const agora = sucesso.resultado.itens.find((recebido) => recebido.notaCompraItemId === itemId);
    return agora
      ? {
          quantidadeNfComercial: agora.quantidadeNfComercial,
          quantidadeEstoqueEsperada: agora.quantidadeEstoqueEsperada,
          quantidadeEstoqueFisica: agora.quantidadeEstoqueFisica,
          quantidadeEquivalentePedido: agora.quantidadeEquivalentePedido,
          localizacaoNome: nomeDoLocal(agora.localizacaoId),
          custoUnitarioEntrada: agora.custoUnitarioEntrada,
          estornadoFisico: 0,
        }
      : null;
  };
  const itemAlvoEstorno = alvoEstorno ? estado.itens.find((candidato) => candidato.notaCompraItemId === alvoEstorno.itemId) : undefined;
  const alvoDoDialogo: ItemEstornoAlvo | null = itemAlvoEstorno
    ? { notaCompraItemId: itemAlvoEstorno.notaCompraItemId, codigoMaterial: itemAlvoEstorno.material?.codigo ?? itemAlvoEstorno.codigoProduto, unidade: itemAlvoEstorno.material?.unidadeEstoque ?? '',
        disponivelFisico: disponivelParaEstorno(recebidoDoItem(itemAlvoEstorno.notaCompraItemId) ?? { quantidadeEstoqueFisica: 0, estornadoFisico: 0 }),
      }
    : null;
  const itemVincular = vincularItemId ? estado.itens.find((candidato) => candidato.notaCompraItemId === vincularItemId) : undefined;
  const itemFator = fatorItemId ? estado.itens.find((candidato) => candidato.notaCompraItemId === fatorItemId) : undefined;
  const itemCadastrar = cadastrarItemId ? estado.itens.find((candidato) => candidato.notaCompraItemId === cadastrarItemId) : undefined;
  const resumo = resumoRodape(estado);
  const habilitado = podeConfirmar(estado) && podeReceber;
  const semPedido = semPedidoEfetivo(estado) && !pedidoDaNotaPendente(estado);
  const fornecedorNovo = !somenteLeitura && fornecedor !== null && estado.fornecedorSituacao === 'A_CONFIRMAR';

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6 pb-28">
      <Breadcrumb aria-label="Trilha">
        <BreadcrumbList>
          <BreadcrumbItem>Compras</BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/compras/notas">Notas de Compra</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>NF {nota.numero}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold leading-tight">Conferência da NF {nota.numero}</h1>
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${situacao.classes}`}>{situacao.rotulo}</span>
            {propostaDaNota && (permissoes.includes('omie:consultar') || permissoes.includes('financeiro:aprovar-fiscal')) && (
              <Button type="button" size="sm" onClick={() => setRevisaoOmieAberta(true)}>Revisar proposta Omie</Button>
            )}
            {modoRevisaoFiscal && <span className="text-xs text-muted-foreground">Modo de revisão fiscal: receber e estornar a nota ficam com Compras.</span>}
          </div>
          <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">NF · série</dt>
              <dd className="tabular-nums">
                {nota.numero} · {nota.serie ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Emitente</dt>
              <dd>
                {emitente.razaoSocial}
                <span className="block text-xs text-muted-foreground tabular-nums">{formatarCnpj(emitente.cnpj)}</span>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Emissão</dt>
              <dd className="tabular-nums">{formatarData(nota.emissao)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Valor total</dt>
              <dd className="tabular-nums">{formatarMoeda(nota.valorTotal)}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-muted-foreground">Chave de acesso</dt>
              <dd className="flex flex-wrap items-center gap-2">
                <span className="tabular-nums" title={nota.chaveAcesso}>
                  {chaveAbreviada(nota.chaveAcesso)}
                </span>
                <Button type="button" variant="outline" size="sm" onClick={() => void copiarChave()}>
                  <Copy aria-hidden="true" />
                  Copiar chave de acesso
                </Button>
                {chaveCopiada && (
                  <span role="status" className="text-xs text-emerald-700">
                    Chave copiada.
                  </span>
                )}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {fornecedorNovo && fornecedor && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <UserCheck className="size-4 shrink-0" aria-hidden="true" />
          <span className="flex-1">
            Fornecedor novo: {fornecedor.razaoSocial} ({formatarCnpj(fornecedor.cnpj)}) ainda não está cadastrado. Confirme os dados para continuar.
          </span>
          {podeGerenciar ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setFornecedorAberto(true)}>
              Confirmar fornecedor
            </Button>
          ) : (
            <span className="text-xs">Peça a quem gerencia Compras para confirmar o fornecedor.</span>
          )}
        </div>
      )}
      {!somenteLeitura && fornecedor === null && (
        <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Esta nota está sem fornecedor. Não é possível receber.
        </div>
      )}
      {!somenteLeitura && fornecedor !== null && estado.fornecedorSituacao === 'INATIVO' && (
        <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          O fornecedor desta nota está inativo. Reative o cadastro para receber.
        </div>
      )}

      {mensagemEstorno && (
        <div role="status" className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
          <span className="flex-1">{mensagemEstorno}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setMensagemEstorno(null)}>
            Dispensar
          </Button>
        </div>
      )}

      {sucesso && (
        <div role="status" className="flex flex-col gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <p className="flex items-center gap-2 font-medium">
            <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
            {sucesso.resultado.itens.length === 1
              ? 'Recebimento registrado. 1 item entrou no estoque.'
              : `Recebimento registrado. ${sucesso.resultado.itens.length} itens entraram no estoque.`}
          </p>
          <ul className="flex flex-col gap-1 pl-6">
            {sucesso.linhas.map((linha) => (
              <li key={linha.notaCompraItemId} className="tabular-nums">
                {linha.texto}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!somenteLeitura && (
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold leading-tight">Pedidos em aberto deste fornecedor</h2>
              <p className="text-sm text-muted-foreground">Sugerimos os pedidos abertos do mesmo CNPJ. Confirme ou ajuste item a item.</p>
            </div>
            <ul className="flex flex-col gap-1">
              {estado.pedidos.map((pedido) => {
                const idCampo = `pedido-${pedido.pedidoId}`;
                const prazo = pedido.prazoGeral ? ` · prazo ${formatarData(pedido.prazoGeral).slice(0, 5)}` : '';
                return (
                  <li key={pedido.pedidoId}>
                    <label htmlFor={idCampo} className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 rounded-md px-2 text-sm hover:bg-slate-100">
                      <Checkbox
                        id={idCampo}
                        checked={estado.pedidosMarcados.includes(pedido.pedidoId)}
                        onCheckedChange={(marcado) => setEstado((atual) => (atual ? alternarPedido(atual, pedido.pedidoId, marcado === true) : atual))}
                      />
                      <span className="tabular-nums">
                        {pedido.numero} · {pedido.itensComSaldo} {pedido.itensComSaldo === 1 ? 'item' : 'itens'} com saldo{prazo}
                      </span>
                    </label>
                  </li>
                );
              })}
              <li>
                <label htmlFor="nota-sem-pedido" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 rounded-md px-2 text-sm hover:bg-slate-100">
                  <Checkbox
                    id="nota-sem-pedido"
                    checked={estado.semPedido}
                    onCheckedChange={(marcado) => setEstado((atual) => (atual ? definirSemPedido(atual, marcado === true) : atual))}
                  />
                  Esta nota não tem pedido
                </label>
              </li>
            </ul>
            {semPedido && (
              <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <p className="flex items-center gap-2">
                  <FileWarning className="size-4 shrink-0" aria-hidden="true" />
                  Compra sem pedido. Informe a justificativa para continuar.
                </p>
                <Label htmlFor="justificativa-sem-pedido">Justificativa (obrigatória)</Label>
                <Textarea
                  id="justificativa-sem-pedido"
                  value={estado.justificativaSemPedido}
                  maxLength={500}
                  rows={2}
                  onChange={(e) => setEstado((atual) => (atual ? { ...atual, justificativaSemPedido: e.target.value } : atual))}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="gap-0 overflow-hidden py-0">
        <table className="w-full text-sm">
          <caption className="sr-only">Itens da NF {nota.numero}</caption>
          <thead className="bg-slate-100 text-left">
            <tr>
              <th scope="col" className={ROTULO_COLUNA}>Item da nota</th>
              <th scope="col" className={ROTULO_COLUNA}>Matéria-prima</th>
              <th scope="col" className={`${ROTULO_COLUNA} text-right`}>Quantidade a receber</th>
              <th scope="col" className={ROTULO_COLUNA}>Alertas</th>
            </tr>
          </thead>
          <tbody>
            {estado.itens.map((item) => (
              <ConferenciaItemLinha
                key={item.notaCompraItemId}
                estado={estado}
                item={item}
                somenteLeitura={somenteLeitura}
                recebido={recebidoDoItem(item.notaCompraItemId)}
                podeCadastrarMaterial={podeCadastrarMaterial}
                erro={errosItem[item.notaCompraItemId] ?? null}
                aoAtualizar={(parcial) => setEstado((atual) => (atual ? atualizarItem(atual, item.notaCompraItemId, parcial) : atual))}
                aoDigitarFisica={(texto) => setEstado((atual) => (atual ? definirFisica(atual, item.notaCompraItemId, texto) : atual))}
                aoVincular={() => setVincularItemId(item.notaCompraItemId)}
                aoCadastrar={() => setCadastrarItemId(item.notaCompraItemId)}
                aoDefinirFator={() => setFatorItemId(item.notaCompraItemId)}
                acoesRecebido={
                  podeGerenciar
                    ? {
                        aoEstornar: () => setAlvoEstorno({ tipo: 'estornar', itemId: item.notaCompraItemId }),
                        aoDevolver: () => setAlvoEstorno({ tipo: 'devolver', itemId: item.notaCompraItemId }),
                      }
                    : null
                }
              />
            ))}
          </tbody>
        </table>
      </Card>

      {aviso && (
        <div role="alert" className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="flex-1">{aviso}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setAviso(null)}>
            Dispensar
          </Button>
        </div>
      )}

      <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-background/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center gap-3 px-6 py-3">
          <div className="flex flex-1 flex-col text-sm">
            <span className="font-medium tabular-nums">
              {resumo.quantidadeItens} {resumo.quantidadeItens === 1 ? 'item' : 'itens'} a receber · {formatarMoeda(resumo.total)}
            </span>
            {!somenteLeitura && motivoDoBloqueio(estado) && <span className="text-xs text-amber-700">{motivoDoBloqueio(estado)}</span>}
            {!somenteLeitura && !podeReceber && <span className="text-xs text-muted-foreground">Você não tem permissão para confirmar o recebimento.</span>}
          </div>
          <Button type="button" variant="secondary" onClick={() => navegar('/compras/notas')}>
            Voltar
          </Button>
          {!somenteLeitura && (
            <Button
              type="button"
              disabled={!habilitado}
              onClick={() => {
                setErroEnvio(null);
                setConfirmacaoAberta(true);
              }}
            >
              Confirmar recebimento
            </Button>
          )}
        </div>
      </div>

      <Dialog open={confirmacaoAberta} onOpenChange={(aberto) => !enviando && setConfirmacaoAberta(aberto)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar recebimento</DialogTitle>
            <DialogDescription>{textoConfirmacao(resumo.quantidadeItens)}</DialogDescription>
          </DialogHeader>
          {erroEnvio && (
            <div role="alert" className="flex flex-col gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              <p>{erroEnvio.mensagem}</p>
              <p className="text-xs">
                {erroEnvio.semEntrada ? 'Nenhuma entrada foi feita. ' : ''}Suas escolhas e justificativas foram mantidas.
              </p>
              {(erroEnvio.tipo === 'conflito' || !erroEnvio.semEntrada) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={() => {
                    setConfirmacaoAberta(false);
                    void carregar();
                  }}
                >
                  <RefreshCw aria-hidden="true" />
                  Recarregar nota
                </Button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" disabled={enviando} onClick={() => setConfirmacaoAberta(false)}>
              Voltar
            </Button>
            <Button type="button" disabled={enviando} onClick={() => void confirmarRecebimento()}>
              {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
              Confirmar recebimento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EstornarEntradaDialog
        aberto={alvoEstorno?.tipo === 'estornar' && alvoDoDialogo !== null}
        onAbertoChange={(aberto) => !aberto && setAlvoEstorno(null)}
        notaId={nota.id}
        item={alvoDoDialogo}
        onConcluido={aposEstorno}
        onFalha={setAviso}
      />

      <DevolverFornecedorDialog
        aberto={alvoEstorno?.tipo === 'devolver' && alvoDoDialogo !== null}
        onAbertoChange={(aberto) => !aberto && setAlvoEstorno(null)}
        notaId={nota.id}
        item={alvoDoDialogo}
        onConcluido={aposEstorno}
        onFalha={setAviso}
      />

      <VincularMaterialDialog
        aberto={itemVincular !== undefined}
        onAbertoChange={(aberto) => !aberto && setVincularItemId(null)}
        fornecedorId={fornecedor?.id ?? null}
        fornecedorNome={fornecedor?.razaoSocial ?? emitente.razaoSocial}
        codigoProduto={itemVincular?.codigoProduto ?? ''}
        aoEscolher={(material) => itemVincular && aplicarMaterial(itemVincular.notaCompraItemId, material)}
      />

      <DefinirFatorDialog
        aberto={itemFator !== undefined}
        onAbertoChange={(aberto) => !aberto && setFatorItemId(null)}
        material={itemFator?.material ?? null}
        unidadeComercial={itemFator?.unidadeComercial ?? ''}
        podeConfigurar={podeConfigurarEstoque}
        aoSalvar={(material) => itemFator && aplicarMaterial(itemFator.notaCompraItemId, material)}
      />

      {itemCadastrar && (
        <MaterialFormModal
          material={{ id: '', codigo: itemCadastrar.codigoProduto, nome: itemCadastrar.descricao, kind: 'INSUMO', status: 'ATIVO', cellCount: 0, ativo: true, createdAt: '' }}
          onClose={() => setCadastrarItemId(null)}
          onSuccess={(criado) => {
            const itemId = itemCadastrar.notaCompraItemId;
            if (criado) void selecionarMaterialCriado(itemId, criado);
            else setAviso('A matéria-prima foi cadastrada. Use "Vincular matéria-prima" para selecioná-la.');
          }}
        />
      )}

      {fornecedor && (
        <FornecedorSheet
          aberto={fornecedorAberto}
          onAbertoChange={setFornecedorAberto}
          fornecedorId={fornecedor.id}
          notaNumero={nota.numero}
          aoSalvar={(salvo) => {
            setFornecedorAberto(false);
            if (salvo.situacao === 'ATIVO') setEstado((atual) => (atual ? confirmarFornecedorLocal(atual) : atual));
          }}
        />
      )}

      {propostaDaNota && (
        <OmieEntradaReviewDialog
          aberto={revisaoOmieAberta}
          onAbertoChange={setRevisaoOmieAberta}
          propostaFiscalId={propostaDaNota}
          permissoes={permissoes}
        />
      )}
    </div>
  );
}
